import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

// API base - set `REACT_APP_API_BASE` in .env if needed
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

export default function Renewal({ userid }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    id: null,
    name: '',
    gender: 'Male',
    mobile: '',
    aadhar: '',
    epic_no: '',
    vidhansabha: 'Karad South',
    karad: { village: '', ganName: '', zpName: '', reference: '' },
    other: { village: '', taluka: '', district: '' },
    booth_no: '',
    formDate: '',
    ackNo: '',
    wfcDate: '',
    wfcLocation: 'Koyana Vasahat',
    wfcOtherLocation: '',
    wfcRemarks: '',
    operator: '',
  });
  const [editing, setEditing] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    village: '',
    ganName: '',
    zpName: '',
    reference: '',
    formDate: '',
    ackNo: '',
    operator: ''
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // fetch records on mount
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/renewal-records`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRecords((data || []).map(mapFromApi));
    } catch (err) {
      console.error('fetchRecords:', err);
    }
  }, []);

  function mapFromApi(a) {
    return {
      id: a.id,
      name: a.name,
      gender: a.gender,
      mobile: a.mobile,
      aadhar: a.aadhar,
      epic_no: a.epic_no,
      vidhansabha: a.vidhansabha,
      karad: {
        village: a.vidhansabha === 'Karad South' ? (a.village || '') : '',
        ganName: a.gan_name || '',
        zpName: a.zp_name || '',
        reference: a.reference || '',
      },
      other: {
        village: a.vidhansabha !== 'Karad South' ? (a.village || '') : '',
        taluka: a.taluka || '',
        district: a.district || '',
      },
      booth_no: a.booth_no,
      formDate: a.form_date || '',
      ackNo: a.ack_no || '',
      wfcDate: a.wfc_date || '',
      wfcLocation: a.wfc_location || 'Koyana Vasahat',
      wfcOtherLocation: a.wfc_other_location || '',
      wfcRemarks: a.wfc_remarks || '',
      operator: a.operator || '',
    };
  }

  function mapToApi(f, uid) {
    return {
      userid: uid,
      name: f.name,
      gender: f.gender,
      mobile: f.mobile,
      aadhar: f.aadhar,
      epic_no: f.epic_no || null,
      vidhansabha: f.vidhansabha,
      village: f.vidhansabha === 'Karad South' ? f.karad.village : f.other.village,
      booth_no: f.booth_no || null,
      gan_name: f.karad.ganName || null,
      zp_name: f.karad.zpName || null,
      reference: f.karad.reference || null,
      taluka: f.other.taluka || null,
      district: f.other.district || null,
      form_date: f.formDate || null,
      ack_no: f.ackNo || null,
      wfc_date: f.wfcDate || null,
      wfc_location: f.wfcLocation,
      wfc_other_location: f.wfcOtherLocation || null,
      wfc_remarks: f.wfcRemarks || null,
      operator: f.operator || null,
    };
  }

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [k]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [k]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    (async () => {
      try {
        if (editing) {
          const res = await fetch(`${API_BASE}/renewal-records/${form.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mapToApi(form, userid)),
          });
          if (!res.ok) throw new Error('Update failed');
        } else {
          const res = await fetch(`${API_BASE}/renewal-records`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mapToApi(form, userid)),
          });
          if (!res.ok) throw new Error('Create failed');
        }
        await fetchRecords();
        resetForm();
        setDrawerOpen(false);
      } catch (err) {
        console.error('handleSubmit:', err);
        alert('Save failed');
      }
    })();
  }

  const handleEdit = useCallback((id) => {
    const rec = records.find((r) => r.id === id);
    if (rec) {
      setForm({ ...rec });
      setEditing(true);
      setDrawerOpen(true);
    }
  }, [records]);

  const handleDelete = useCallback((id) => {
    if (!window.confirm('Delete this record?')) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/renewal-records/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Delete failed');
        await fetchRecords();
      } catch (err) {
        console.error('handleDelete:', err);
        alert('Delete failed');
      }
    })();
  }, [fetchRecords]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }, []);

  const filteredRecords = useMemo(() => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
    return records.filter((r) => {
      return (
        m(r.name).includes(m(filters.name)) &&
        m(r.mobile).includes(m(filters.mobile)) &&
        m(r.aadhar).includes(m(filters.aadhar)) &&
        (m(r.karad?.village).includes(m(filters.village)) || m(r.other?.village).includes(m(filters.village))) &&
        m(r.karad?.ganName).includes(m(filters.ganName)) &&
        m(r.karad?.zpName).includes(m(filters.zpName)) &&
        m(r.karad?.reference).includes(m(filters.reference)) &&
        m(r.formDate).includes(m(filters.formDate)) &&
        m(r.ackNo).includes(m(filters.ackNo)) &&
        m(r.operator).includes(m(filters.operator))
      );
    });
  }, [records, filters]);

  const handleDownloadExcel = () => {
    const dataToExport = filteredRecords.map(r => ({
      Name: r.name,
      Gender: r.gender,
      Mobile: r.mobile,
      Aadhar: r.aadhar,
      'EPIC No': r.epic_no,
      Vidhansabha: r.vidhansabha,
      Village: r.karad?.village || r.other?.village,
      'Booth No': r.booth_no,
      'Gan Name': r.karad?.ganName,
      'ZP Name': r.karad?.zpName,
      Reference: r.karad?.reference,
      Taluka: r.other?.taluka,
      District: r.other?.district,
      'Form Date': r.formDate,
      'Ack No': r.ackNo,
      'WFC Date': r.wfcDate,
      'WFC Location': r.wfcLocation,
      'WFC Other Location': r.wfcOtherLocation,
      'WFC Remarks': r.wfcRemarks,
      Operator: r.operator,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Renewals');
    XLSX.writeFile(workbook, `Renewals_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  function resetForm() {
    setForm({
      id: null,
      name: '',
      gender: 'Male',
      mobile: '',
      aadhar: '',
      epic_no: '',
      vidhansabha: 'Karad South',
      karad: { village: '', ganName: '', zpName: '', reference: '' },
      other: { village: '', taluka: '', district: '' },
      booth_no: '',
      formDate: '',
      ackNo: '',
      wfcDate: '',
      wfcLocation: 'Koyana Vasahat',
      wfcOtherLocation: '',
      wfcRemarks: '',
      operator: '',
    });
    setEditing(false);
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', fontWeight: 'bold' }}>Renewal Form Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="success"
            onClick={handleDownloadExcel}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', mr: 1 }}
          >
            Download Excel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Renewal
          </Button>
        </Box>

        {useMemo(() => (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Mobile', 'Aadhar', 'Village', 'Gan Name', 'Zp Name', 'Reference', 'Form Date', 'Ack No', 'Operator', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="village" value={filters.village} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="ganName" value={filters.ganName} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="zpName" value={filters.zpName} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="reference" value={filters.reference} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="formDate" value={filters.formDate} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="ackNo" value={filters.ackNo} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="operator" value={filters.operator} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((r) => (
                  <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                    <TableCell>{r.mobile}</TableCell>
                    <TableCell>{r.aadhar}</TableCell>
                    <TableCell>{r.karad?.village || r.other?.village}</TableCell>
                    <TableCell>{r.karad?.ganName}</TableCell>
                    <TableCell>{r.karad?.zpName}</TableCell>
                    <TableCell>{r.karad?.reference}</TableCell>
                    <TableCell>{r.formDate}</TableCell>
                    <TableCell>{r.ackNo}</TableCell>
                    <TableCell>{r.operator}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)} sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ), [filteredRecords, filters, handleFilterChange, handleEdit, handleDelete])}
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Renewal' : 'Add New Renewal'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="MH No" name="epic_no" value={form.epic_no} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.vidhansabha === 'Karad South' ? (
              <Box sx={{ borderLeft: '3px solid #00acc1', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="karad.village" value={form.karad.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Gan Name" name="karad.ganName" value={form.karad.ganName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="ZP Name" name="karad.zpName" value={form.karad.zpName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Reference" name="karad.reference" value={form.karad.reference} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            ) : (
              <Box sx={{ borderLeft: '3px solid #8e24aa', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="other.village" value={form.other.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            )}

            <TextField label="Taluka" name="other.taluka" value={form.other.taluka} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="District" name="other.district" value={form.other.district} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="Form Date" name="formDate" type="date" InputLabelProps={{ shrink: true }} value={form.formDate} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Acknowledgement No." name="ackNo" value={form.ackNo} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="WFC Visit Date" name="wfcDate" type="date" InputLabelProps={{ shrink: true }} value={form.wfcDate} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="wfc-loc-label">WFC Visit Location</InputLabel>
              <Select labelId="wfc-loc-label" label="WFC Visit Location" name="wfcLocation" value={form.wfcLocation} onChange={handleChange}>
                <MenuItem value="Koyana Vasahat">Koyana Vasahat</MenuItem>
                <MenuItem value="Banawadi">Banawadi</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.wfcLocation === 'Other' && (
              <TextField label="Other WFC Location" name="wfcOtherLocation" value={form.wfcOtherLocation} onChange={handleChange} fullWidth variant="outlined" />
            )}

            <TextField label="WFC Visit Remarks" name="wfcRemarks" value={form.wfcRemarks} onChange={handleChange} multiline rows={3} fullWidth variant="outlined" />

            <TextField label="Operator Name" name="operator" value={form.operator} onChange={handleChange} fullWidth variant="outlined" />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }}>
              {editing ? 'Update' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
